import React from 'react';
import { PhoneCall, ChevronRight, CheckCircle2, TrendingUp, Sparkles } from 'lucide-react';
import { ArrowRightIcon } from '@radix-ui/react-icons';
import { AuroraText } from './magicui/AuroraText';
import { AnimatedShinyText } from './magicui/AnimatedShinyText';
import { AnimatedGradientText } from './magicui/AnimatedGradientText';
import { NumberTicker } from './magicui/NumberTicker';
import { TextAnimate } from './magicui/TextAnimate';
import { ShimmerButton } from './magicui/ShimmerButton';
import { HexagonPattern } from './magicui/HexagonPattern';
import copyData from '../../docs/copy/landing_page_copy.json';

import kianFaceImg from '../assets/kian-face-cropped.png';

export function Hero({ onOpenBooking }) {
  // Reusable Stats Bar component
  const StatsBar = ({ className }) => (
    <div className={className}>
      <div>
        <div className="text-lg sm:text-2xl font-extrabold text-amber-600 dark:text-amber-400">
          <NumberTicker value={35} className="text-amber-600 dark:text-amber-400" />+
        </div>
        <div className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400 font-semibold">Mentored Students</div>
      </div>

      <div>
        <div className="text-lg sm:text-2xl font-extrabold text-slate-900 dark:text-white">
          <NumberTicker value={90} className="text-slate-900 dark:text-white" />%+
        </div>
        <div className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400 font-semibold">Target Average</div>
      </div>

      <div>
        <div className="text-lg sm:text-2xl font-extrabold text-amber-500 dark:text-amber-300">
          <NumberTicker value={100} className="text-amber-500 dark:text-amber-300" />%
        </div>
        <div className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400 font-semibold">Active Recall</div>
      </div>
    </div>
  );

  return (
    <section className="relative overflow-hidden pt-3 pb-6 sm:pt-6 sm:pb-10 rounded-3xl">
      {/* Magic UI Hexagon Pattern Background */}
      <HexagonPattern
        radius={36}
        className="stroke-slate-200/60 fill-transparent dark:stroke-slate-800/40 opacity-70"
      />

      {/* Asymmetric 60/40 Split Hero Above-The-Fold Layout */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center">
        
        {/* Left Column (Copy, CTAs, Risk Reversal & Desktop Stats) */}
        <div className="lg:col-span-7 space-y-4 sm:space-y-5 text-left">
          
          {/* Kicker Badge (Hidden on mobile) */}
          {copyData.hero.kicker && (
            <div className="hidden sm:inline-flex items-center">
              <div className="group relative rounded-full border border-amber-500/30 bg-white/90 dark:bg-slate-900/90 px-3.5 py-1 text-xs text-slate-800 dark:text-slate-200 transition-all shadow-xs">
                <AnimatedShinyText className="inline-flex items-center gap-1.5 font-bold text-amber-700 dark:text-amber-300">
                  <span>{copyData.hero.kicker}</span>
                  <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                </AnimatedShinyText>
              </div>
            </div>
          )}

          {/* Bold TextAnimate & Aurora Headline */}
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.15] sm:leading-[1.1] space-y-1">
            <TextAnimate
              animation="blurInUp"
              by="word"
              as="div"
              className="text-slate-900 dark:text-white"
            >
              {copyData.hero.headline_kinetic}
            </TextAnimate>
            
            <div>
              <AuroraText>
                <TextAnimate
                  animation="blurInUp"
                  by="word"
                  delay={0.25}
                  as="div"
                >
                  {copyData.hero.headline_aurora}
                </TextAnimate>
              </AuroraText>
            </div>
          </h1>

          {/* Subtext Rule Formula */}
          <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed font-normal max-w-xl">
            <span className="sm:hidden">
              We pair your Grade 10–12 teen with top McMaster scholars. Active recall &amp; STEM mastery → 90%+ admissions averages.
            </span>
            <span className="hidden sm:inline">
              {copyData.hero.subtext_rule_formula}
            </span>
          </p>

          {/* CTAs with Magic UI ShimmerButton */}
          <div className="space-y-2 pt-1">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <ShimmerButton
                onClick={() => { window.location.href = 'tel:+14374382820'; }}
                shimmerColor="#ffffff"
                background="rgba(234, 179, 8, 1)"
                className="w-full sm:w-auto text-xs sm:text-sm px-5 sm:px-6 py-3 sm:py-3.5 shadow-md shadow-amber-500/20"
              >
                <PhoneCall className="h-4 w-4 mr-2" /> {copyData.hero.cta_primary}
              </ShimmerButton>

              <button
                onClick={onOpenBooking}
                className="group relative flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 sm:px-6 py-3 sm:py-3.5 shadow-sm transition-all duration-300 hover:bg-slate-100 hover:scale-[1.02] active:scale-[0.98] dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800 w-full sm:w-auto cursor-pointer"
              >
                <AnimatedGradientText className="text-xs sm:text-sm font-bold">
                  {copyData.hero.cta_secondary}
                </AnimatedGradientText>
                <ChevronRight className="ml-1 h-4 w-4 text-slate-500 transition-transform duration-300 ease-in-out group-hover:translate-x-0.5" />
              </button>
            </div>

            {/* Risk Reversal Line */}
            <div className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400 font-medium">
              <div className="sm:hidden space-y-0.5">
                <div>✓ 100% Free 15-Min Diagnostic • No Obligation</div>
                <div>✓ Speak Directly with an Active McMaster Scholar</div>
              </div>
              <div className="hidden sm:block">
                {copyData.hero.risk_reversal_line}
              </div>
            </div>
          </div>

          {/* Desktop Stats Bar */}
          <StatsBar className="hidden lg:grid grid-cols-3 gap-4 pt-4 border-t border-slate-200 dark:border-slate-800/80" />

        </div>

        {/* Right Column: Structured Scholar Verification Card + Mobile Stats Bar */}
        <div className="lg:col-span-5 relative flex flex-col items-center gap-4">
          <div className="relative w-full max-w-[380px] sm:max-w-[420px] rounded-3xl border border-slate-200 bg-white/90 dark:border-slate-800 dark:bg-slate-900/90 p-4 sm:p-5 shadow-2xl backdrop-blur-md space-y-3.5">
            
            {/* 1. Card Header */}
            <div className="flex items-center gap-3.5 pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-full border-2 border-amber-400 p-0.5 flex-shrink-0 bg-slate-900 shadow-md">
                <img
                  src={kianFaceImg}
                  alt="Kian Ahadi — McMaster Neuroscience Scholar"
                  className="h-full w-full rounded-full object-cover"
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white leading-none">Kian Ahadi</h3>
                  <CheckCircle2 className="h-4 w-4 text-amber-500 fill-amber-500/20" />
                </div>
                
                <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10px] sm:text-[11px] font-bold">
                  McMaster Neuroscience Scholar
                </div>

                <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span>Available for Grade 10-12 Matching</span>
                </div>
              </div>
            </div>

            {/* 2. Peer Advantage Badge */}
            <div className="p-3 sm:p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 flex-shrink-0" />
                <div>
                  <div className="text-xs font-extrabold text-slate-900 dark:text-white">Low Age-Gap Peer Mentor</div>
                  <div className="text-[10px] sm:text-[11px] font-medium text-slate-600 dark:text-slate-400">Relatable Mentor vs Distant Tutor</div>
                </div>
              </div>
              <span className="text-[11px] sm:text-xs font-black text-amber-600 dark:text-amber-400 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg bg-white dark:bg-slate-900 border border-amber-400/30">
                Age 20 vs 55+
              </span>
            </div>

            {/* 3. Student Transformation Metric Proof Card */}
            <div className="space-y-2">
              <div className="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Proven Grade Transformation
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {/* Before */}
                <div className="p-2.5 sm:p-3 rounded-xl bg-slate-100 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 space-y-1">
                  <div className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400">Before Mentorship</div>
                  <div className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300">Memorization</div>
                  <div className="text-xs font-black text-red-600 dark:text-red-400">74% Average</div>
                </div>

                {/* After */}
                <div className="p-2.5 sm:p-3 rounded-xl bg-amber-500/10 dark:bg-amber-500/20 border border-amber-400/40 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] sm:text-[10px] font-bold text-amber-700 dark:text-amber-300">After Active Recall</span>
                    <TrendingUp className="h-3.5 w-3.5 text-amber-500" />
                  </div>
                  <div className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white">Active Recall</div>
                  <div className="text-xs font-black text-amber-600 dark:text-amber-400">92% Average</div>
                </div>
              </div>
            </div>

            {/* 4. Footer Trust Strip (University Logos Only) */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
              <div className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Target Acceptance Cut-Offs
              </div>
              
              <div className="grid grid-cols-4 gap-1.5 items-center">
                <div className="flex flex-col items-center gap-1 p-1 sm:p-1.5 rounded-xl bg-slate-50 dark:bg-slate-950/40 border border-slate-200/60 dark:border-slate-800">
                  <img src="/assets/logos/mcmaster.svg" alt="McMaster Health Sci logo" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" />
                  <span className="text-[8px] sm:text-[9px] font-bold text-slate-700 dark:text-slate-300 text-center leading-none whitespace-nowrap">McMaster Health</span>
                </div>

                <div className="flex flex-col items-center gap-1 p-1 sm:p-1.5 rounded-xl bg-slate-50 dark:bg-slate-950/40 border border-slate-200/60 dark:border-slate-800">
                  <img src="/assets/logos/uoft.svg" alt="UofT Engineering logo" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" />
                  <span className="text-[8px] sm:text-[9px] font-bold text-slate-700 dark:text-slate-300 text-center leading-none whitespace-nowrap">UofT Eng</span>
                </div>

                <div className="flex flex-col items-center gap-1 p-1 sm:p-1.5 rounded-xl bg-slate-50 dark:bg-slate-950/40 border border-slate-200/60 dark:border-slate-800">
                  <img src="/assets/logos/waterloo.svg" alt="Waterloo CS logo" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" />
                  <span className="text-[8px] sm:text-[9px] font-bold text-slate-700 dark:text-slate-300 text-center leading-none whitespace-nowrap">Waterloo CS</span>
                </div>

                <div className="flex flex-col items-center gap-1 p-1 sm:p-1.5 rounded-xl bg-slate-50 dark:bg-slate-950/40 border border-slate-200/60 dark:border-slate-800">
                  <img src="/assets/logos/mcgill.svg" alt="McGill logo" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" />
                  <span className="text-[8px] sm:text-[9px] font-bold text-slate-700 dark:text-slate-300 text-center leading-none whitespace-nowrap">McGill Pre-Med</span>
                </div>
              </div>
            </div>

          </div>

          {/* Mobile Stats Bar */}
          <StatsBar className="w-full max-w-[380px] grid lg:hidden grid-cols-3 gap-3 p-4 rounded-2xl bg-white/80 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 backdrop-blur-md text-center shadow-md" />
        </div>

      </div>
    </section>
  );
}
